/**
 * Exact decimal-string helpers for off-chain INCPT amounts.
 * Avoids IEEE-754 Number for compare / min / max of large balances
 * (e.g. $100k+ presale allocations with up to 18 fractional digits).
 */

/** Canonical non-negative decimal (no leading zeros except "0.…"). */
const CANONICAL_RE = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;
/** Accepts leading zeros so UI paste / MAX can be normalized. */
const LOOSE_RE = /^\d+(?:\.\d+)?$/;

export function isDecimalString(value: string): boolean {
  return CANONICAL_RE.test(value.trim());
}

/** Normalize "01.2300" → "1.23", "0.0" → "0". Rejects invalid / negative. */
export function normalizeDecimalString(value: string, maxScale = 18): string | null {
  const raw = value.trim();
  if (!LOOSE_RE.test(raw)) return null;
  const parts = raw.split('.');
  const wholeRaw = parts[0] ?? '0';
  const fracRaw = parts[1] ?? '';
  if (fracRaw.length > maxScale) return null;
  const whole = wholeRaw.replace(/^0+(?=\d)/, '') || '0';
  const frac = fracRaw.replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}

/** Scale a non-negative decimal string to a BigInt at `maxScale` fraction digits. */
export function decimalToScaled(value: string, maxScale = 18): bigint | null {
  const norm = normalizeDecimalString(value, maxScale);
  if (norm == null) return null;
  const parts = norm.split('.');
  const whole = parts[0] ?? '0';
  const frac = parts[1] ?? '';
  const fracPadded = (frac + '0'.repeat(maxScale)).slice(0, maxScale);
  try {
    return BigInt(whole) * 10n ** BigInt(maxScale) + BigInt(fracPadded || '0');
  } catch {
    return null;
  }
}

export function compareDecimalStrings(a: string, b: string, maxScale = 18): -1 | 0 | 1 | null {
  const sa = decimalToScaled(a, maxScale);
  const sb = decimalToScaled(b, maxScale);
  if (sa == null || sb == null) return null;
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
}

export function decimalGte(a: string, b: string, maxScale = 18): boolean {
  const c = compareDecimalStrings(a, b, maxScale);
  return c !== null && c >= 0;
}

export function decimalGt(a: string, b: string, maxScale = 18): boolean {
  const c = compareDecimalStrings(a, b, maxScale);
  return c === 1;
}

export function decimalIsPositive(value: string, maxScale = 18): boolean {
  return decimalGt(value, '0', maxScale);
}

/** Format for UI lists without going through Number (keeps integer part exact).
 *  Default 2 fraction digits — matches INCPT purchase-history style. */
export function formatDecimalDisplay(value: string, maxFrac = 2): string {
  const norm = normalizeDecimalString(value, 18);
  if (norm == null) return value;
  const parts = norm.split('.');
  const whole = parts[0] ?? '0';
  const frac = parts[1] ?? '';
  const wholeFmt = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (!frac || maxFrac <= 0) return wholeFmt;
  const trimmed = frac.slice(0, maxFrac).replace(/0+$/, '');
  return trimmed ? `${wholeFmt}.${trimmed}` : wholeFmt;
}

/**
 * Approximate USD for portfolio display only. Not for debit/credit decisions.
 * Uses Number — acceptable for UI tiles, never for send validation.
 */
export function decimalToApproxNumber(value: string): number {
  const norm = normalizeDecimalString(value, 18);
  if (norm == null) return NaN;
  return Number(norm);
}
